import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogContentText,
  Badge,
  Alert,
  Avatar,
  Rating,
} from '@mui/material';
import { taskService } from '../../services/taskService';
import { bidService } from '../../services/bidService';
import api from '../../services/api';
import { Task, TaskStatus } from '../../types/task';
import { Bid } from '../../types/bid';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { translateCategory } from '../../utils/categoryTranslations';
import { translateTag } from '../../utils/tagTranslations';
import { formatCurrency } from '../../utils/currency';
import PageBackButton from '../../components/PageBackButton';

interface TaskWithBidsCount extends Task {
  bidsCount?: number;
}

interface BidProfessionalInfo {
  id: number;
  name: string;
  avatar_url?: string;
  rating: number;
  total_reviews: number;
  latest_review_comment?: string;
}

const MyTasks: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [tasks, setTasks] = useState<TaskWithBidsCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [bidsDialogOpen, setBidsDialogOpen] = useState(false);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [bidProfessionals, setBidProfessionals] = useState<Record<number, BidProfessionalInfo>>({});

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedTasks = await taskService.getMyTasks();
      const tasksWithBids = await Promise.all(
        fetchedTasks.map(async (task) => {
          const taskBids = await bidService.getBidsByTaskId(task.id);
          return { ...task, bidsCount: taskBids.length };
        })
      );
      setTasks(tasksWithBids);
    } catch (error) {
      console.error('Error loading tasks:', error);
      enqueueSnackbar(t('errors.failedToLoadTasks'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [t, enqueueSnackbar]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleViewBids = async (task: Task) => {
    try {
      setBidsLoading(true);
      setSelectedTask(task);
      const taskBids = await bidService.getBidsByTaskId(task.id);
      console.log('Задача:', task);
      console.log('Статус на задачата:', task.status);
      console.log('Оферти:', taskBids);
      setBids(taskBids);
      await loadBidProfessionals(taskBids);
      setBidsDialogOpen(true);
    } catch (error) {
      setError(t('errors.failedToLoadBids'));
      console.error('Error loading bids:', error);
    } finally {
      setBidsLoading(false);
    }
  };

  const handleAcceptBid = async (bidId: number) => {
    try {
      console.log(`Започвам процеса на приемане на оферта с ID: ${bidId}`);
      setLoading(true);
      const acceptedBid = await bidService.acceptBid(bidId);
      console.log(`Оферта успешно приета`, acceptedBid);

      await loadTasks();
      setBidsDialogOpen(false);
      setConfirmDialogOpen(false);
      setSuccessDialogOpen(true);

      enqueueSnackbar(t('notifications.bidAccepted'), { variant: 'success' });
      
      // Пренасочваме към детайлите на задачата
      if (selectedTask?.id) {
        navigate(`/tasks/${selectedTask.id}`);
      }
    } catch (error: any) {
      console.error(`Грешка при приемане на оферта:`, error);
      let errorMessage = t('errors.failedToAcceptBid');
      
      if (error.message === 'Офертата вече не е активна') {
        errorMessage = t('errors.bidNotActive');
        // Презареждаме списъка с оферти, за да покажем актуалния статус
        await loadTasks();
      }
      
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setBidsDialogOpen(false);
  };

  const resolveProfessionalIdFromBid = (bid: Bid): number | null => {
    const rawId = bid.user_id ?? bid.professional_id;
    const normalizedId = Number(rawId);
    return Number.isFinite(normalizedId) && normalizedId > 0 ? normalizedId : null;
  };

  const loadBidProfessionals = async (taskBids: Bid[]) => {
    const professionalIds = Array.from(
      new Set(
        taskBids
          .map(resolveProfessionalIdFromBid)
          .filter((id): id is number => id !== null)
      )
    );

    if (professionalIds.length === 0) {
      setBidProfessionals({});
      return;
    }

    const entries = await Promise.all(
      professionalIds.map(async (professionalId) => {
        try {
          const response = await api.get(`/api/v1/professionals/${professionalId}`);
          const profile = response.data || {};
          const info: BidProfessionalInfo = {
            id: professionalId,
            name: profile.name || profile.full_name || profile.email || `#${professionalId}`,
            avatar_url: profile.avatar_url || '',
            rating: Number(profile.rating || 0),
            total_reviews: Number(profile.total_reviews || 0),
            latest_review_comment: profile.reviews?.[0]?.comment || '',
          };
          return [professionalId, info] as const;
        } catch (fetchError) {
          console.error(`Error loading professional profile ${professionalId}:`, fetchError);
          return null;
        }
      })
    );

    const mapped = entries.filter((entry): entry is readonly [number, BidProfessionalInfo] => entry !== null);
    setBidProfessionals(Object.fromEntries(mapped));
  };

  const getStatusColor = (status: TaskStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'open':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: TaskStatus): string => {
    return t(`tasks.list.filter.${status}`);
  };

  const getLocale = () => getDateFnsLocale(i18n.language);

  const filteredTasks = tasks.filter(task => 
    statusFilter === 'all' ? true : task.status === statusFilter
  );

  if (loading) {
    return (
      <Container className="py-8 text-center">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <PageBackButton />
        </Box>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="py-8">
      <PageBackButton />
      <Box className="flex justify-between items-center mb-6">
        <Typography variant="h4" component="h1">
          {t('tasks.list.my_tasks_title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/tasks/create')}
        >
          {t('dashboard.create_task')}
        </Button>
      </Box>

      <Box className="mb-4 flex gap-2 flex-wrap">
        <Button
          variant={statusFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('all')}
          size="small"
        >
          {t('tasks.list.filter.all')}
        </Button>
        <Button
          variant={statusFilter === 'open' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('open')}
          size="small"
          color="info"
        >
          {t('tasks.list.filter.open')}
        </Button>
        <Button
          variant={statusFilter === 'in_progress' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('in_progress')}
          size="small"
          color="warning"
        >
          {t('tasks.list.filter.in_progress')}
        </Button>
        <Button
          variant={statusFilter === 'completed' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('completed')}
          size="small"
          color="success"
        >
          {t('tasks.list.filter.completed')}
        </Button>
        <Button
          variant={statusFilter === 'cancelled' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('cancelled')}
          size="small"
          color="error"
        >
          {t('tasks.list.filter.cancelled')}
        </Button>
      </Box>

      {filteredTasks.length === 0 ? (
        <Typography color="textSecondary" className="text-center">
          {statusFilter === 'all' 
            ? t('common.no_results')
            : t('tasks.list.no_tasks_with_status', { status: getStatusText(statusFilter as TaskStatus).toLowerCase() })}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredTasks.map((task) => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <Card className="h-full flex flex-col">
                <CardContent className="flex-grow">
                  <Box className="flex justify-between items-start mb-3">
                    <Typography variant="h6" component="h2" className="mb-2 flex-grow">
                      {task.title}
                    </Typography>
                    <Chip
                      label={getStatusText(task.status)}
                      color={getStatusColor(task.status)}
                      size="small"
                      className="ml-2"
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary" className="mb-3 line-clamp-2">
                    {task.description}
                  </Typography>
                  <Box className="grid grid-cols-2 gap-2 mb-3">
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {t('tasks.list.card.budget')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(task.budget, i18n.language, task.currency)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {t('tasks.list.card.deadline')}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {format(new Date(task.deadline), 'Pp', { locale: getLocale() })}
                      </Typography>
                    </Box>
                  </Box>
                  {task.address && (
                    <Box className="mb-3">
                      <Typography variant="caption" color="textSecondary" display="block">
                        {t('tasks.list.card.location')}
                      </Typography>
                      <Typography variant="body2">
                        {task.address}
                      </Typography>
                    </Box>
                  )}
                  
                  {task.category && (
                    <Box className="mb-2">
                      <Typography variant="caption" color="textSecondary" display="block">
                        {t('tasks.details.category')}
                      </Typography>
                      <Typography variant="body2">
                        {translateCategory(task.category.name, i18n.language)}
                      </Typography>
                    </Box>
                  )}
                  
                  {task.tags && task.tags.length > 0 && (
                    <Box className="mb-2">
                      <Typography variant="caption" color="textSecondary" display="block" className="mb-1">
                        {t('tasks.details.tags')}
                      </Typography>
                      <Box className="flex flex-wrap">
                        {task.tags.map(tag => (
                          <Chip 
                            key={tag.id} 
                            label={translateTag(tag.name, i18n.language)} 
                            size="small" 
                            variant="outlined"
                            className="mr-1 mb-1" 
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
                <CardActions className="flex justify-between">
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/tasks/${task.id}`)}
                  >
                    {t('tasks.list.card.view_details')}
                  </Button>
                  {task.status === 'open' && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => handleViewBids(task)}
                      startIcon={<Badge badgeContent={task.bidsCount || 0} color="primary" />}
                    >
                      {t('tasks.list.card.view_bids')}
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={bidsDialogOpen}
        onClose={() => setBidsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="bids-dialog-title"
      >
        <DialogTitle id="bids-dialog-title">
          {t('tasks.list.bids.title')} - {selectedTask?.title}
        </DialogTitle>
        <DialogContent>
          {bidsLoading ? (
            <Box className="flex justify-center p-4">
              <CircularProgress />
            </Box>
          ) : bids.length === 0 ? (
            <Typography color="textSecondary" className="text-center p-4">
              {t('tasks.list.card.no_bids')}
            </Typography>
          ) : (
            <List>
              {bids.map((bid) => (
                <ListItem key={bid.id} divider>
                  <ListItemText
                    primary={`${t('bids.list.card.amount')}: ${formatCurrency(bid.amount, i18n.language, bid.currency)}`}
                    secondary={
                      <Box component="span">
                        <Typography variant="body2" component="span" color="textSecondary" display="block">
                          {bid.description}
                        </Typography>
                        <Typography variant="caption" component="span" color="textSecondary" display="block">
                          {format(new Date(bid.created_at), 'Pp', { locale: getLocale() })}
                        </Typography>
                        {(() => {
                          const professionalId = resolveProfessionalIdFromBid(bid);
                          if (!professionalId) return null;

                          const professional = bidProfessionals[professionalId];
                          if (!professional) return null;

                          const avatarUrl = professional.avatar_url?.startsWith('http')
                            ? professional.avatar_url
                            : professional.avatar_url
                              ? `${window.location.origin}${professional.avatar_url.startsWith('/') ? professional.avatar_url : `/${professional.avatar_url}`}`
                              : '';

                          return (
                            <Box
                              component="span"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                mt: 1,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: 'action.hover',
                              }}
                            >
                              <Avatar
                                src={avatarUrl}
                                alt={professional.name}
                                sx={{ width: 34, height: 34 }}
                              >
                                {professional.name?.[0]?.toUpperCase() || '?'}
                              </Avatar>
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                                  {professional.name}
                                </Typography>
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Rating value={professional.rating} precision={0.1} size="small" readOnly />
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    ({professional.total_reviews})
                                  </Typography>
                                </Box>
                                {professional.latest_review_comment && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      maxWidth: 260,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    "{professional.latest_review_comment}"
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
                                  onClick={() => navigate(`/profile/professional/${professional.id}`)}
                                >
                                  {t('common.my_profile', 'Профил')}
                                </Button>
                              </Box>
                            </Box>
                          );
                        })()}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    {selectedTask?.status === 'open' && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAcceptBid(bid.id)}
                        disabled={bid.status !== 'pending'}
                        aria-label={t('bids.list.card.accept')}
                      >
                        {t('bids.list.card.accept')}
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBidsDialogOpen(false)} 
            color="primary"
            aria-label={t('common.close')}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          {t('tasks.list.bids.confirm_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('tasks.list.bids.confirm_message')}
          </DialogContentText>
          <Box mt={2}>
            <Typography variant="subtitle1">
              {t('bids.list.card.amount')}: {selectedBid ? formatCurrency(selectedBid.amount, i18n.language, selectedBid.currency) : '-'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedBid?.description}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => selectedBid && handleAcceptBid(selectedBid.id)}
            color="primary"
            variant="contained"
            autoFocus
          >
            {loading ? <CircularProgress size={24} /> : t('bids.list.card.accept')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">
          {t('tasks.list.bids.success_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('tasks.list.bids.success_message')}
          </DialogContentText>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              {t('tasks.list.bids.next_steps')}:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary={t('tasks.list.bids.next_step_1')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('tasks.list.bids.next_step_2')} />
              </ListItem>
              <ListItem>
                <ListItemText primary={t('tasks.list.bids.next_step_3')} />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} color="primary">
            {t('common.close')}
          </Button>
          <Button
            onClick={() => selectedTask?.id && navigate(`/tasks/${selectedTask.id}`)}
            color="primary"
            variant="contained"
          >
            {t('tasks.list.card.view_details')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyTasks; 